import errorReporter from '@app/errorReporter';

const DECIMAL = '.';

const TENTH_DIGIT_LENGTH = 2;

/**
 * Outputs a number formatted as a currency.
 * Assumes whole numbers are dollars, numbers after decimals are cents.
 * E.g. 1234.5 becomes the string "$1,234.50".
 * Defaults to Canadian currency unless { currency: 'USD' } is passed.
 * Returns undefined if bad input (non-number)
 * @param input A number or string representing a number to format.
 * @param options Any of the regular TypeScript currency formatting options. E.g. { currency: 'USD' }
 * @returns The number string formatted with a dollar sign and commas. E.g. "$1,000"
 */
const formatCurrency = (
  input: string | number,
  options?: Intl.NumberFormatOptions,
): string | undefined => {
  if (!input && input !== 0) return undefined;
  const number = typeof input === 'number' ? input : Number(input);

  if (Number.isNaN(number)) {
    const err = new Error(
      `Please provide a valid input to be formatted. Provided input: ${input}, cannot be coerced to number.`,
    );
    errorReporter.error(err);
  }

  const currency = options?.currency || 'CAD';
  const locale = currency === 'USD' ? 'en-US' : 'en-CA';

  const formatted = number?.toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  });

  if (
    formatted.length >= TENTH_DIGIT_LENGTH &&
    formatted[formatted.length - TENTH_DIGIT_LENGTH] === DECIMAL
  ) {
    return `${formatted}0`;
  }

  return formatted;
};

export default formatCurrency;
